<script lang="ts" setup>
	export type BenefitHighlightContext = {
		title: string
		description: string
		image: string
		tag_img?: string
		bg?: string
		open?: boolean
	}[]

	export type BenefitHighlightContextClass = {
		li: Partial<{
			activeClass: string
			border: string
			activeText: string
			inactive: string
		}>
		ul: Partial<{
			width: string
			margin: string
			flexBasis: string
		}>
	}

	const configClass: BenefitHighlightContextClass = {
		li: {
			activeClass: 'border-base-600',
			activeText: 'text-base-600 dark:text-base-300',
			border: 'border-b-[2px]',
			inactive: 'border-gray-300 dark:border-gray-600'
		},
		ul: {
			width: 'w-full  max-w-[580px]',
			margin: 'mx-auto lg:mx-0 ',
			flexBasis: 'flex-1'
		}
	}
	const { ul, li } = configClass

	const props = defineProps<{
		highlights: BenefitHighlightContext
		ui?: Partial<BenefitHighlightContextClass>
	}>()
	const list = ref<BenefitHighlightContext>([])
	const selected = shallowRef('')

	watch(
		() => props.highlights,
		() => {
			list.value = props.highlights
			selected.value = list.value?.[0]?.title ?? ''
		},
		{ immediate: true }
	)
</script>

<template>
	<section>
		<div class="flex items-center w-full justify-between gap-8">
			<ul :class="[ui?.ul?.width ?? ul?.width, ui?.ul?.flexBasis ?? ul?.flexBasis, ui?.ul?.margin ?? ul?.margin]">
				<li
					v-for="(item, index) in list"
					:key="'article-' + index"
					class="sm:pt-6 py-3 border-b"
					:class="[
						selected === item.title
							? [ui?.li?.activeClass ?? li?.activeClass, ui?.li?.border ?? li.border]
							: [ui?.li?.inactive ?? li?.inactive]
					]"
				>
					<button
						type="button"
						class="flex items-center gap-x-4 pt-3 justify-between w-full text-left custom-transition-2"
						:aria-expanded="selected === item.title"
						@click="selected = item.title"
					>
						<span
							class="font-semibold"
							:class="[
								selected === item.title
									? [ui?.li?.activeText ?? li.activeText, 'md:text-2xl text-xl']
									: 'dark:text-gray-300 text-lg md:text-xl'
							]"
						>
							{{ item.title ?? '---' }}
						</span>
					</button>

					<div
						class="custom-transition-2 ease-in-out overflow-hidden pointer-events-none"
						:class="{ 'max-h-0': selected != item.title, 'max-h-[1000px]': selected === item.title }"
					>
						<div class="pt-[10px] sm:pt-2 relative">
							<p class="text-base md:text-lg max-w-[620px] text-gray-700 dark:text-gray-500">
								{{ item.description }}
							</p>

							<div class="min-h-[298px] md:hidden">
								<transition-group
									name="animate"
									enter-active-class="animate__animated animate__fadeIn"
									leave-active-class="animate__animated animate__fast animate__fadeOut"
								>
									<template v-if="item.title === selected">
										<nuxt-img
											:src="item.image"
											provider="cloudinary"
											loading="lazy"
											:alt="item?.title ?? 'user-profile'"
											class="text-sm object-fill sm:max-h-[298px] mt-5 rounded-[12px] bg-gray-300"
											:class="{
												inactiveSlide: item.title !== selected,
												activeSlide: item.title === selected
											}"
										/>
									</template>
								</transition-group>
							</div>
						</div>
					</div>
				</li>
			</ul>

			<div
				class="max-w-[584px] w-full hidden sticky top-[12rem] lg:flex flex-col space-y-4 rounded-[20px] overflow-hidden"
			>
				<template v-for="(item, index) in list" :key="index">
					<transition-group
						name="animate"
						enter-active-class="animate__animated animate__fadeInUp animate__faster"
					>
						<template v-if="item.title === selected">
							<nuxt-img
								provider="cloudinary"
								:src="item.image"
								:alt="item?.title ?? 'user-profile'"
								class="text-sm object-fill rounded-[20px] max-w-[inherit] bg-gray-300"
								loading="lazy"
								:class="{
									inactiveSlide: item.title !== selected,
									activeSlide: item.title === selected
								}"
							/>
						</template>
					</transition-group>
				</template>
			</div>
		</div>
	</section>
</template>

<style scoped>
	.inactiveSlide {
		@apply translate-y-[100%] absolute pointer-events-none;
	}
	.activeSlide {
		@apply translate-y-0;
	}
</style>
